import { useTheme } from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { useQuery } from 'react-query';
import { countryTranslation } from './utils/countryTranslation';
import { fetchIPAddressLocation } from './utils/ipAddressLocation';
import { Box } from '@material-ui/core';

interface PhoneNoInputProps {
  onChange: (value: string) => void;
  defaultCountryCode?: string;
  required?: boolean;
  error?: boolean;
  defaultNumber?: string;
}

interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
  format: string;
}

const KEY_SUFFIX = Date.now().toString();

/**
 * Renders a Phone Number Input component that detects
 * @param labelText - optional The text for the label that is positioned above the input
 * @param defaultCountryCode - optional 2 letter country code prop, defaults to gb
 */
const PhoneNoInput: React.FC<PhoneNoInputProps> = ({
  defaultCountryCode = 'gb',
  required = false,
  error = false,
  onChange,
  defaultNumber,
}: PhoneNoInputProps) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const localization = countryTranslation[i18n.language.substr(0, 2).toLowerCase()];
  const formattedCountryCode = defaultCountryCode.toString().toLowerCase();
  const [countryCode, setCountryCode] = useState(formattedCountryCode);

  // Fetch the two letter country code
  useQuery(
    'ipLookup',
    () => {
      if (!defaultNumber) return fetchIPAddressLocation();
    },
    {
      onSuccess: (data) => setCountryCode(data ? data.country_code.toLowerCase() : formattedCountryCode),
    },
  );

  useEffect(() => {
    const flagDropDownElement = document.querySelector('.selected-flag');

    if (flagDropDownElement) {
      flagDropDownElement.setAttribute('tabindex', '-1');
    }
  }, []);

  return (
    <Box
      sx={{
        '& .form-control, .react-tel-input > .form-control': {
          backgroundColor: theme.palette.grey['100'],

          ':hover': {
            backgroundColor: theme.palette.grey['300'],
            background: theme.palette.grey['300'],
            transition: 'background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
            borderRadius: '0px',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
          },
          ':focus': {
            backgroundColor: theme.palette.grey['200'],
          },
        },
      }}
    >
      <PhoneInput
        key={`${i18n.language}_${KEY_SUFFIX}`} // without this the localized values will not change
        localization={localization}
        value={defaultNumber}
        enableLongNumbers={15}
        onChange={(phone, data: CountryData) => {
          if (phone) {
            const dialLen = data.dialCode.length;

            if (data.dialCode && phone[dialLen] === '0')
              onChange(`+${phone.slice(0, dialLen)}${phone.slice(dialLen + 1)}`);
            else onChange(`+${phone}`);
          } else {
            onChange('');
          }
        }}
        country={countryCode}
        specialLabel=""
        placeholder={t('ns.common:phone_input:placeholder')}
        inputProps={{ id: 'phone', 'data-testid': 'phone', required }}
        containerStyle={{
          fontFamily: `${theme.typography.fontFamily}`,
          width: '100%',
        }}
        inputStyle={{
          font: 'inherit',
          width: '100%',
          height: '56px',
          borderTop: 0,
          borderRight: 0,
          borderLeft: 0,
          borderBottom: `${error ? `2px solid ${theme.palette.error.dark}` : `1px solid ${theme.palette.grey['500']}`}`,
          borderRadius: 0,
          boxShadow: 'none',
        }}
        buttonStyle={{
          borderRadius: 0,
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
          borderBottom: `${error ? `2px solid ${theme.palette.error.dark}` : `1px solid ${theme.palette.grey['500']}`}`,
        }}
        dropdownStyle={{
          borderRadius: 0,
          marginTop: `${error ? `2px` : `1px`}`,
          textAlign: 'left',
        }}
      />
    </Box>
  );
};

export default PhoneNoInput;
